<script setup>
import dayjs from 'dayjs'
import { computed, inject, ref, toValue } from 'vue'

import {
  getProgressModuleGranulationSettingsById,
  PROGRESS_MODULE_CELL_PLACEMENTS
} from '@/utils/custom-dashboard-helper'
import { getStartOfTheDay } from '@/utils/date'
import {
  ELEMENT_PROGRESS_CELL_VIEW_TYPES,
  ELEMENT_PROGRESS_GRADE_CELL_COLORS,
  ELEMENT_PROGRESS_STATUSES,
  ELEMENT_PROGRESS_VALUE_CELL_COLORS
} from '@/utils/element-progress'
import { TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  currentModuleStateInjectionKey,
  customDashboardStateInjectionKey
} from '@/utils/injection-keys'
import {
  currentUserCanUpdateObjective,
  isOkrElementClosed,
  okrElementGradeIsEditable
} from '@/utils/objectives'
import { includesValue } from '@/utils/prop-validators'

import ProgressModuleGridCellContent from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridCellContent'

defineOptions({
  name: 'ProgressModuleGridCell'
})

const props = defineProps({
  cellData: {
    type: Object,
    required: true
  },

  element: {
    type: Object,
    required: true
  },

  hasReadAccess: {
    type: Boolean
  },

  placement: {
    type: String,
    default: PROGRESS_MODULE_CELL_PLACEMENTS.CUSTOM_DASHBOARD,
    validator: v => includesValue(v, PROGRESS_MODULE_CELL_PLACEMENTS)
  }
})

const cellOptions = computed(() => {
  if (props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.VALUE_AND_THRESHOLD) {
    return (
      Object.values(ELEMENT_PROGRESS_STATUSES).find(
        options => options.statusId === props.cellData.data.statusId
      ) || ELEMENT_PROGRESS_STATUSES.NO_VALUE
    )
  }

  if (props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.VALUE) {
    return {
      colors: ELEMENT_PROGRESS_VALUE_CELL_COLORS
    }
  }

  return {
    colors: ELEMENT_PROGRESS_GRADE_CELL_COLORS
  }
})

const currentModuleState = inject(currentModuleStateInjectionKey, ref({}))

const isPacedInOkrElementForm = computed(
  () => props.placement === PROGRESS_MODULE_CELL_PLACEMENTS.OKR_ELEMENT_FORM
)

const isPeriodToShort = computed(() => {
  if (isPacedInOkrElementForm.value) return false

  const { minPeriodDurationToShowContent } = getProgressModuleGranulationSettingsById({
    granulationId: toValue(currentModuleState).currentGranulationId
  })
  return props.cellData.duration < minPeriodDurationToShowContent
})

const isFuturePeriod = computed(() => {
  const today = getStartOfTheDay(dayjs())
  const cellStartDate = getStartOfTheDay(
    dayjs(props.cellData[TARGET_PERIOD_ENTITY_KEYS.START_DATE])
  )

  return Number(cellStartDate) > Number(today)
})

const customDashboardState = inject(customDashboardStateInjectionKey, ref({}))

const isReadMode = computed(() => {
  if (isPacedInOkrElementForm.value) return false
  return Boolean(customDashboardState.value.isReadMode)
})

const updatable = computed(() => {
  if (isReadMode.value) return false

  if (isPeriodToShort.value) return false

  if (props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.GRADE) return false

  return (
    // !isFuturePeriod.value &&
    currentUserCanUpdateObjective(props.element) &&
    okrElementGradeIsEditable(props.element) &&
    !isOkrElementClosed(props.element)
  )
})

const isPlaceholderCell = computed(() => {
  return props.cellData.viewType === ELEMENT_PROGRESS_CELL_VIEW_TYPES.PLACEHOLDER
})

const emit = defineEmits({
  'update:is-loading': null
})
</script>

<template>
  <div
    :class="{
      'pmgc-Cell-updatable': updatable && !isPlaceholderCell,
      'pmgc-Cell-future': isFuturePeriod
    }"
    :style="{
      ...cellOptions.colors,
      flex: cellData.duration
    }"
    class="pmgc-Cell"
  >
    <ProgressModuleGridCellContent
      v-if="!isPeriodToShort"
      :cell-data
      :element="element"
      :has-read-access
      :is-future-period
      :placement
      :updatable
      @loading="emit('update:is-loading', $event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.pmgc-Cell {
  overflow: hidden;
  background-color: var(--pm-background-color, #{$white});
  --resolved-divider-size: var(--pm-divider-size, var(--cdpm-divider-size));
  --resolved-divider-color: var(--pm-border-color, var(--cdpm-divider-color));
  --resolved-text-color: var(--pm-text-color, #{$dark-3});

  color: var(--resolved-text-color);

  --right-border: inset calc(-1 * var(--cdpm-divider-size)) 0 0 var(--cdpm-divider-color);
  --bottom-border: inset 0 calc(-1 * var(--cdpm-divider-size)) 0 var(--cdpm-divider-color);

  box-shadow: var(--bottom-border);

  transition: box-shadow $transition-fast;

  &-future {
    --resolved-text-color: var(--pm-text-in-future-color, #{$grey-1-next});
  }

  &:not(:empty) {
    // border-bottom: var(--divider-size, 1px) solid var(--pm-border-color, #{$grey-2-next});
    --bottom-border: inset 0 calc(var(--resolved-divider-size) * -1) 0 var(--resolved-divider-color);
  }

  &:empty {
    background-color: lighten($grey-3-next, 2%);
  }

  &:not(:last-child) {
    box-shadow: var(--right-border), var(--bottom-border);
  }

  &-updatable {
    @media (any-hover: hover) {
      &:hover {
        --resolved-divider-size: 3px;
        --resolved-divider-color: var(--pm-border-hover-color, #{$dark-2});
      }
    }
  }
}
</style>
